import { useCallback, useEffect, useMemo, useState } from "react";

import { FileText, Plus, User } from "lucide-react";
import moment from "moment";
import { toast } from "sonner";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import Pagination from "../../../components/shared/Pagination";
import SearchInput from "../../../components/shared/SearchInput";
import TruncatedText from "../../../components/shared/TruncatedText";
import { Button } from "../../../components/ui/button.jsx";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { Textarea } from "../../../components/ui/textarea.jsx";
import { useApi } from "../../../context/ApiContext";
import { useConfirm } from "../../../context/ConfirmContext.jsx";
import { usePagination } from "../../../hooks/usePagination";

const NotesTab = ({ selectedProject, user, users }) => {
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const api = useApi();
  const pagination = usePagination(10);
  const { items: notes, currentPage: page, pageSize, setPaginationData } = pagination;

  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const [activeSearchText, setActiveSearchText] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");

  const permissions = useMemo(
    () => ({
      canCreate: user?.permissions?.notes?.create && !selectedProject?.deleted,
      canUpdate: user?.permissions?.notes?.update && !selectedProject?.deleted,
      canDelete: user?.permissions?.notes?.delete && !selectedProject?.deleted
    }),
    [user?.permissions?.notes, selectedProject?.deleted]
  );

  const loadNotes = useCallback(
    async (projectId) => {
      try {
        const response = await api.get(`/notes/projects/${projectId}`, {
          params: {
            page,
            size: pageSize,
            search: activeSearchText,
            sortBy,
            direction
          }
        });
        setPaginationData(response.data);
      } catch (error) {
        console.error("Failed to load notes:", error);
        if (error.response?.status === 403) {
          setPaginationData({ items: [], totalItems: 0, totalPages: 0 });
        }
      }
    },
    [api, page, pageSize, setPaginationData, activeSearchText, sortBy, direction]
  );

  const createNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      const currDate = new Date();
      const formattedDate = moment(currDate).format("YYYY-MM-DDTHH:mm:ss");
      await api.post(`/notes`, {
        projectId: selectedProject.id,
        content: newNote.trim(),
        createdAt: formattedDate,
        createdBy: user.id,
        updatedAt: formattedDate
      });
      loadNotes(selectedProject.id);
      setNewNote("");
      setIsAddNoteOpen(false);
      toast.success("Note added successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to add note");
    }
  };

  const updateNote = async (noteId, content) => {
    try {
      await api.put(`/notes/${noteId}`, { content });
      loadNotes(selectedProject.id);
      setEditingNote(null);
      toast.success("Note updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to update note");
    }
  };

  const confirm = useConfirm();

  const deleteNote = async (noteId) => {
    const isConfirmed = await confirm({
      title: "Delete Note",
      message: "Are you sure you want to delete this note?"
    });
    if (isConfirmed) {
      try {
        await api.delete(`/notes/${noteId}`);
        loadNotes(selectedProject.id);
        toast.success("Note deleted successfully!");
      } catch (error) {
        toast.error(error.message || "Failed to delete note");
      }
    }
  };

  useEffect(() => {
    if (selectedProject?.id) {
      loadNotes(selectedProject.id);
    }
  }, [selectedProject, loadNotes]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-yale-blue-500" />
          Project Notes
        </h3>

        <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto">
          <SearchInput placeholder="Search notes..." onSearch={setActiveSearchText} className="sm:w-64" />
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Dialog open={isAddNoteOpen} onOpenChange={setIsAddNoteOpen}>
              {permissions.canCreate && (
                <Button
                  variant="primary"
                  className="h-10 px-4 rounded-xl flex items-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-95"
                  onClick={() => setIsAddNoteOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                  <span className="font-medium text-sm">Add Note</span>
                </Button>
              )}
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Project Note</DialogTitle>
                  <DialogDescription>Record an important update or observation for this project.</DialogDescription>
                </DialogHeader>
                <form onSubmit={createNote} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="note_content">Content</Label>
                    <Textarea
                      id="note_content"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Type your note here..."
                      rows={5}
                      required
                      className="rounded-xl border-slate-200 dark:border-slate-800"
                    />
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button variant="primary" type="submit" className="w-full py-6 rounded-xl font-bold">
                      Post Note
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Edit Note Dialog - Moved outside header for cleaner structure */}
      <Dialog open={!!editingNote} onOpenChange={() => setEditingNote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>Update your project note</DialogDescription>
          </DialogHeader>
          {editingNote && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateNote(editingNote.id, editingNote.content);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="edit_note_content">Note Content</Label>
                <Textarea
                  id="edit_note_content"
                  value={editingNote.content}
                  onChange={(e) =>
                    setEditingNote({
                      ...editingNote,
                      content: e.target.value
                    })
                  }
                  rows={5}
                  required
                  className="rounded-xl border-slate-200 dark:border-slate-800"
                />
              </div>
              <div className="flex justify-end pt-4">
                <Button variant="primary" type="submit" className="w-full py-6 rounded-xl font-bold">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <DataTable
          columns={[
            {
              header: "Note Content",
              accessor: "content",
              isPrimary: true,
              wrap: true,
              render: (val) => (
                <div className="max-w-[250px] md:max-w-[350px]">
                  <TruncatedText
                    text={val}
                    maxLength={60}
                    className="text-sm text-yale-blue-800 dark:text-slate-200 leading-relaxed"
                  />
                </div>
              )
            },
            {
              header: "Author",
              accessor: "createdBy",
              render: (val, row) => {
                const authorName = row.creator?.name || row.createdBy?.name || "Unknown";
                return (
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-slate-100 dark:bg-slate-800 rounded-full">
                      <User className="w-3 h-3 text-slate-500" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{authorName}</span>
                  </div>
                );
              }
            },
            {
              header: "Date",
              accessor: "createdAt",
              sortable: true,
              sortKey: "createdAt",
              render: (val) => (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {new Date(val).toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </span>
              )
            },
            {
              header: "Actions",
              accessor: "id",
              render: (_, note) => (
                <ActionMenu
                  canEdit={permissions.canUpdate}
                  canDelete={permissions.canDelete}
                  onEdit={() => setEditingNote(note)}
                  onDelete={() => deleteNote(note.id)}
                />
              )
            }
          ]}
          data={notes}
          emptyStateTitle="No notes yet"
          emptyStateDescription="Record important field notes or updates about this project."
          emptyStateIcon={FileText}
          sortBy={sortBy}
          direction={direction}
          onSort={(key, dir) => {
            setSortBy(key);
            setDirection(dir);
          }}
        />
      </div>

      <Pagination pagination={pagination} />
    </div>
  );
};

export default NotesTab;
